import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessagesSquare,
  UserPlus,
  CalendarClock,
  PhoneCall,
  LogOut,
  Home,
  BarChart3,
  Check,
  X,
  Settings2,
  Download,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function LangPill({ lang }) {
  const isKr = lang === "ht";
  return (
    <span
      className={
        "font-label text-[9px] px-1.5 py-0.5 rounded border " +
        (isKr
          ? "bg-orange-100 text-orange-800 border-orange-200"
          : "bg-blue-100 text-blue-800 border-blue-200")
      }
    >
      {isKr ? "KR" : "EN"}
    </span>
  );
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("conversations");
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [calls, setCalls] = useState([]);
  const [twilioConfig, setTwilioConfig] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [convDetail, setConvDetail] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetail, setCallDetail] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, c, l, a, ca, tw] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/conversations"),
        api.get("/admin/leads"),
        api.get("/admin/appointments"),
        api.get("/admin/calls"),
        api.get("/twilio/config"),
      ]);
      setStats(s.data);
      setConversations(c.data);
      setLeads(l.data);
      setAppointments(a.data);
      setCalls(ca.data);
      setTwilioConfig(tw.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 15000);
    return () => clearInterval(iv);
  }, [loadAll]);

  useEffect(() => {
    if (!selectedConv) {
      setConvDetail(null);
      return;
    }
    api
      .get(`/admin/conversations/${selectedConv}`)
      .then(({ data }) => setConvDetail(data))
      .catch(() => setConvDetail(null));
  }, [selectedConv]);

  useEffect(() => {
    if (!selectedCall) {
      setCallDetail(null);
      return;
    }
    api
      .get(`/admin/calls/${selectedCall}`)
      .then(({ data }) => setCallDetail(data))
      .catch(() => setCallDetail(null));
  }, [selectedCall]);

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const setApptStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Appointment ${status}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const navItems = [
    { key: "conversations", label: "Conversations", icon: MessagesSquare, count: conversations.length },
    { key: "leads", label: "Messages", icon: UserPlus, count: leads.length },
    { key: "appointments", label: "Appointments", icon: CalendarClock, count: appointments.length },
    { key: "calls", label: "Phone Calls", icon: PhoneCall, count: calls.length },
    { key: "twilio", label: "Twilio Setup", icon: Settings2, count: null },
  ];

  return (
    <div className="min-h-screen flex bg-[#F9F6F0]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#1E3B2F] text-white flex flex-col">
        <div className="p-6">
          <div className="font-serif-display text-2xl leading-none">Gentry</div>
          <div className="font-label text-[9px] text-white/60 mt-1">Admin · Gens Realty</div>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {navItems.map((n) => (
            <button
              key={n.key}
              onClick={() => setTab(n.key)}
              data-testid={`admin-nav-${n.key}`}
              className={
                "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors " +
                (tab === n.key
                  ? "bg-[#294d3f] text-white"
                  : "text-white/70 hover:text-white hover:bg-[#294d3f]/60")
              }
            >
              <span className="flex items-center gap-2">
                <n.icon size={16} />
                {n.label}
              </span>
              {n.count !== null && (
                <span className="text-[10px] font-label text-white/60">{n.count}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <a
            href="/"
            className="flex items-center gap-2 text-xs text-white/70 hover:text-white"
            data-testid="admin-back-home"
          >
            <Home size={14} /> Public site
          </a>
          <div className="text-[11px] text-white/50">{user?.email}</div>
          <Button
            onClick={doLogout}
            variant="outline"
            className="w-full h-9 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <header className="px-8 py-6 border-b border-[#EBE4D3] flex items-center justify-between bg-white">
          <div>
            <h1 className="font-serif-display text-3xl text-[#1E3B2F]">
              {navItems.find((n) => n.key === tab)?.label}
            </h1>
            <p className="text-xs text-[#6B7280] mt-1">
              {tab === "conversations" && "All visitor conversations, most recent first."}
              {tab === "leads" && "Messages captured through the chat."}
              {tab === "appointments" && "Appointment requests from visitors."}
            </p>
          </div>
          {stats && (
            <div className="flex items-center gap-6 text-xs">
              <Stat label="Conversations" value={stats.conversations} />
              <Stat label="Messages" value={stats.leads} />
              <Stat label="Appointments" value={stats.appointments} />
              <div className="flex items-center gap-2">
                <BarChart3 size={14} className="text-[#6B7280]" />
                <span className="text-[#6B7280]">
                  EN {stats.language_breakdown.en} · KR {stats.language_breakdown.ht}
                </span>
              </div>
            </div>
          )}
        </header>

        <div className="p-8">
          {tab === "conversations" && (
            <ConversationsView
              conversations={conversations}
              selectedConv={selectedConv}
              setSelectedConv={setSelectedConv}
              convDetail={convDetail}
            />
          )}
          {tab === "leads" && <LeadsView leads={leads} />}
          {tab === "appointments" && (
            <AppointmentsView appointments={appointments} setApptStatus={setApptStatus} />
          )}
          {tab === "calls" && (
            <CallsView
              calls={calls}
              selectedCall={selectedCall}
              setSelectedCall={setSelectedCall}
              callDetail={callDetail}
            />
          )}
          {tab === "twilio" && <TwilioSetupView twilioConfig={twilioConfig} />}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <div className="font-serif-display text-2xl text-[#1E3B2F] leading-none">{value}</div>
      <div className="font-label text-[9px] text-[#6B7280] mt-1">{label}</div>
    </div>
  );
}

function ConversationsView({ conversations, selectedConv, setSelectedConv, convDetail }) {
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 bg-white rounded-xl border border-[#EBE4D3] overflow-hidden">
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-[#EBE4D3]" data-testid="conversations-list">
          {conversations.length === 0 ? (
            <div className="p-6 text-sm text-[#6B7280]">No conversations yet.</div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedConv(c.id)}
                data-testid={`conversation-item-${c.id}`}
                className={
                  "w-full text-left px-4 py-3 transition-colors " +
                  (selectedConv === c.id ? "bg-[#F9F6F0]" : "hover:bg-[#F9F6F0]/70")
                }
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1F1F1F]">
                    {c.visitor_name || "Anonymous visitor"}
                  </div>
                  <LangPill lang={c.language} />
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1 flex justify-between">
                  <span>{c.message_count || 0} messages</span>
                  <span>{fmt(c.updated_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="lg:col-span-3 bg-white rounded-xl border border-[#EBE4D3] p-4">
        {!convDetail ? (
          <div className="h-full flex items-center justify-center text-sm text-[#6B7280] py-16">
            Select a conversation to view messages.
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#EBE4D3]">
              <div>
                <div className="font-medium text-[#1F1F1F]">
                  {convDetail.conversation.visitor_name || "Anonymous visitor"}
                </div>
                <div className="text-xs text-[#6B7280]">
                  {convDetail.conversation.visitor_phone || "No phone captured"}
                </div>
              </div>
              <LangPill lang={convDetail.conversation.language} />
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto chat-scroll">
              {convDetail.messages.map((m) => (
                <div
                  key={m.id}
                  className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}
                >
                  <div
                    className={
                      "max-w-[80%] rounded-2xl px-3 py-2 text-sm " +
                      (m.role === "user"
                        ? "bg-[#C25934] text-white"
                        : "bg-[#F2ECDF] text-[#1F1F1F]")
                    }
                  >
                    <p className="whitespace-pre-wrap">{m.content}</p>
                    <div className="text-[10px] opacity-70 mt-1">{fmt(m.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LeadsView({ leads }) {
  if (leads.length === 0)
    return (
      <div className="space-y-4">
        <div className="flex justify-end"><ExportOptInsButton /></div>
        <div className="text-sm text-[#6B7280]">No messages captured yet.</div>
      </div>
    );
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportOptInsButton />
      </div>
      <div className="bg-white rounded-xl border border-[#EBE4D3] overflow-hidden" data-testid="leads-list">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F6F0] text-left">
            <tr>
              <Th>Name</Th>
              <Th>Phone</Th>
              <Th>Email</Th>
              <Th>Reason</Th>
              <Th>Language</Th>
              <Th>SMS</Th>
              <Th>Received</Th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-[#EBE4D3]" data-testid={`lead-row-${l.id}`}>
                <Td>{l.name}</Td>
                <Td>{l.phone}</Td>
                <Td className="max-w-[220px]"><span className="truncate block">{l.email || "—"}</span></Td>
                <Td className="max-w-md">
                  <span className="line-clamp-2">{l.reason}</span>
                </Td>
                <Td><LangPill lang={l.language} /></Td>
                <Td><SmsPill consent={l.sms_consent} /></Td>
                <Td>{fmt(l.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExportOptInsButton() {
  const download = async () => {
    try {
      const res = await api.get("/admin/opt-ins/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `gentry_sms_opt_ins_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Opt-in CSV downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  return (
    <button
      onClick={download}
      className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#1E3B2F] hover:bg-[#294d3f] text-white text-xs font-medium btn-lift"
      data-testid="export-opt-ins-button"
    >
      <Download size={14} /> Export SMS Opt-Ins (CSV)
    </button>
  );
}

function AppointmentsView({ appointments, setApptStatus }) {
  if (appointments.length === 0)
    return <div className="text-sm text-[#6B7280]">No appointment requests yet.</div>;
  return (
    <div className="bg-white rounded-xl border border-[#EBE4D3] overflow-hidden" data-testid="appointments-list">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F6F0] text-left">
          <tr>
            <Th>Date</Th>
            <Th>Time</Th>
            <Th>Name</Th>
            <Th>Phone</Th>
            <Th>Email</Th>
            <Th>Reason</Th>
            <Th>Lang</Th>
            <Th>SMS</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {appointments.map((a) => (
            <tr key={a.id} className="border-t border-[#EBE4D3]" data-testid={`appointment-row-${a.id}`}>
              <Td>{a.date}</Td>
              <Td>{a.time}</Td>
              <Td>{a.name}</Td>
              <Td>{a.phone}</Td>
              <Td className="max-w-[180px]"><span className="truncate block">{a.email || "—"}</span></Td>
              <Td className="max-w-xs"><span className="line-clamp-2">{a.reason}</span></Td>
              <Td><LangPill lang={a.language} /></Td>
              <Td><SmsPill consent={a.sms_consent} /></Td>
              <Td>
                <StatusBadge status={a.status} />
              </Td>
              <Td>
                <div className="flex gap-1">
                  {a.status !== "confirmed" && (
                    <button
                      onClick={() => setApptStatus(a.id, "confirmed")}
                      className="w-7 h-7 rounded flex items-center justify-center bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      data-testid={`appointment-confirm-${a.id}`}
                    >
                      <Check size={14} />
                    </button>
                  )}
                  {a.status !== "cancelled" && (
                    <button
                      onClick={() => setApptStatus(a.id, "cancelled")}
                      className="w-7 h-7 rounded flex items-center justify-center bg-red-100 text-red-700 hover:bg-red-200"
                      data-testid={`appointment-cancel-${a.id}`}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800",
  };
  return (
    <span className={"font-label text-[9px] px-2 py-1 rounded " + (map[status] || map.pending)}>
      {status}
    </span>
  );
}

function SmsPill({ consent }) {
  return consent ? (
    <span
      className="font-label text-[9px] px-2 py-1 rounded bg-emerald-100 text-emerald-800"
      title="Caller opted in to receive SMS updates"
    >
      OPT-IN
    </span>
  ) : (
    <span
      className="font-label text-[9px] px-2 py-1 rounded bg-neutral-100 text-neutral-500"
      title="No SMS consent — call only, do not text"
    >
      NO SMS
    </span>
  );
}

function Th({ children }) {
  return (
    <th className="font-label text-[10px] text-[#6B7280] px-4 py-3 text-left">{children}</th>
  );
}
function Td({ children, className = "" }) {
  return <td className={"px-4 py-3 text-[#1F1F1F] " + className}>{children}</td>;
}


function CallsView({ calls, selectedCall, setSelectedCall, callDetail }) {
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 bg-white rounded-xl border border-[#EBE4D3] overflow-hidden">
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-[#EBE4D3]" data-testid="calls-list">
          {calls.length === 0 ? (
            <div className="p-6 text-sm text-[#6B7280]">
              No phone calls yet. When callers dial your Twilio number, they&apos;ll appear here.
            </div>
          ) : (
            calls.map((c) => (
              <button
                key={c.call_sid}
                onClick={() => setSelectedCall(c.call_sid)}
                data-testid={`call-item-${c.call_sid}`}
                className={
                  "w-full text-left px-4 py-3 transition-colors " +
                  (selectedCall === c.call_sid ? "bg-[#F9F6F0]" : "hover:bg-[#F9F6F0]/70")
                }
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1F1F1F]">
                    {c.from_number || "Unknown caller"}
                  </div>
                  {c.language ? <LangPill lang={c.language} /> : null}
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1 flex justify-between">
                  <span>
                    {c.outcome || c.status} · {c.turn_count || 0} turns
                  </span>
                  <span>{fmt(c.started_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="lg:col-span-3 bg-white rounded-xl border border-[#EBE4D3] p-4">
        {!callDetail ? (
          <div className="h-full flex items-center justify-center text-sm text-[#6B7280] py-16">
            Select a call to view its transcript.
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#EBE4D3]">
              <div>
                <div className="font-medium text-[#1F1F1F]">
                  {callDetail.call.from_number || "Unknown caller"}
                </div>
                <div className="text-xs text-[#6B7280]">
                  {callDetail.call.status} · outcome: {callDetail.call.outcome || "n/a"}
                </div>
              </div>
              {callDetail.call.language ? <LangPill lang={callDetail.call.language} /> : null}
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto chat-scroll">
              {callDetail.turns.map((t) => (
                <div
                  key={t.id}
                  className={"flex " + (t.role === "user" ? "justify-end" : "justify-start")}
                >
                  <div
                    className={
                      "max-w-[80%] rounded-2xl px-3 py-2 text-sm " +
                      (t.role === "user"
                        ? "bg-[#C25934] text-white"
                        : "bg-[#F2ECDF] text-[#1F1F1F]")
                    }
                  >
                    <p className="whitespace-pre-wrap">{t.text}</p>
                    <div className="text-[10px] opacity-70 mt-1">{fmt(t.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TwilioSetupView({ twilioConfig }) {
  if (!twilioConfig) {
    return <div className="text-sm text-[#6B7280]">Loading…</div>;
  }
  return (
    <div className="max-w-3xl space-y-6" data-testid="twilio-setup-view">
      <div className="bg-white rounded-xl border border-[#EBE4D3] p-6">
        <h2 className="font-serif-display text-2xl text-[#1E3B2F] mb-1">
          Phone (IVR) configuration
        </h2>
        <p className="text-xs text-[#6B7280] mb-5">
          Point your Twilio number&apos;s &ldquo;A CALL COMES IN&rdquo; webhook at the URL below (HTTP POST). Callers
          will hear the bilingual menu and route to the AI receptionist.
        </p>
        <ConfigRow
          label="Voice webhook (POST)"
          value={twilioConfig.webhook_voice_url}
          ok={!!twilioConfig.webhook_voice_url}
        />
        <ConfigRow
          label="Status callback (POST)"
          value={twilioConfig.webhook_status_url}
          ok={!!twilioConfig.webhook_status_url}
        />
        <ConfigRow
          label="Inbound SMS webhook (POST)"
          value={twilioConfig.webhook_sms_url}
          ok={!!twilioConfig.webhook_sms_url}
        />
        <ConfigRow
          label="Twilio phone number"
          value={twilioConfig.twilio_number}
          ok={!!twilioConfig.twilio_number}
        />
        <ConfigRow
          label="Twilio API credentials"
          value={twilioConfig.twilio_configured ? "Account SID + API Key + Secret set" : "Missing"}
          ok={twilioConfig.twilio_configured}
        />
        <ConfigRow
          label="Signature validation"
          value={
            twilioConfig.twilio_signature_validation
              ? "TWILIO_AUTH_TOKEN set"
              : "Off — set TWILIO_AUTH_TOKEN in backend/.env to enable"
          }
          ok={twilioConfig.twilio_signature_validation}
        />
      </div>

      <div className="bg-white rounded-xl border border-[#EBE4D3] p-6">
        <h2 className="font-serif-display text-2xl text-[#1E3B2F] mb-1">Voice quality</h2>
        <p className="text-xs text-[#6B7280] mb-5">
          Without ElevenLabs, callers hear Twilio&apos;s built-in Polly voices (Joanna for English, Léa
          for the Kreyòl fallback). Add an ElevenLabs API key for a natural multilingual voice.
        </p>
        <ConfigRow
          label="ElevenLabs (natural TTS)"
          value={twilioConfig.elevenlabs_configured ? "ELEVENLABS_API_KEY set" : "Not set — using Polly"}
          ok={twilioConfig.elevenlabs_configured}
        />
        <ConfigRow
          label="Google Calendar sync"
          value={
            twilioConfig.gcal_configured
              ? "Service account + Calendar ID set"
              : "Not set — appointments saved to Gentry only"
          }
          ok={twilioConfig.gcal_configured}
        />
        <ConfigRow
          label="SMS delivery (Programmable Messaging)"
          value={
            twilioConfig.sms_configured
              ? "Twilio credentials present — confirmation + reminder texts will send to opted-in leads."
              : "Missing — no SMS will be sent."
          }
          ok={twilioConfig.sms_configured}
        />
        <ConfigRow
          label="Staff email notifications (Resend)"
          value={
            twilioConfig.email_configured
              ? `Sending to ${twilioConfig.email_to} from ${twilioConfig.email_from || "onboarding@resend.dev"}`
              : "Not set — add RESEND_API_KEY in backend/.env to enable."
          }
          ok={twilioConfig.email_configured}
        />
      </div>
    </div>
  );
}

function ConfigRow({ label, value, ok }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-[#EBE4D3] last:border-b-0">
      <div className="font-label text-[10px] text-[#6B7280] shrink-0 w-48 pt-1">{label}</div>
      <div className="flex-1 flex items-center justify-between gap-3">
        <code className="text-[13px] text-[#1F1F1F] break-all">{value || "—"}</code>
        {ok !== undefined && (
          <span
            className={
              "font-label text-[9px] px-2 py-1 rounded shrink-0 " +
              (ok ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")
            }
          >
            {ok ? "READY" : "NOT SET"}
          </span>
        )}
      </div>
    </div>
  );
}
